/**
 * MemoryIntelligencePanel.jsx
 * ============================
 * Cross-document memory and knowledge graph visualization.
 */
import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import KnowledgeGraphVisualization from './KnowledgeGraphVisualization';
import { 
  Database, Search, Network, Users, Building2, 
  FileText, DollarSign, Calendar, MapPin, RefreshCw,
  Loader2, Link2, Brain
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ENTITY_ICONS = {
  person: Users,
  organization: Building2,
  document: FileText,
  amount: DollarSign,
  date: Calendar,
  location: MapPin,
  default: Database
};

const ENTITY_COLORS = {
  person: 'bg-blue-100 text-blue-700 border-blue-300',
  organization: 'bg-purple-100 text-purple-700 border-purple-300',
  document: 'bg-green-100 text-green-700 border-green-300',
  amount: 'bg-yellow-100 text-yellow-700 border-yellow-300',
  date: 'bg-orange-100 text-orange-700 border-orange-300',
  location: 'bg-pink-100 text-pink-700 border-pink-300',
  default: 'bg-gray-100 text-gray-700 border-gray-300'
};

const MemoryIntelligencePanel = () => {
  const [query, setQuery] = useState('');
  const [queryType, setQueryType] = useState('entities');
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [entities, setEntities] = useState([]);
  const [graphData, setGraphData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('search');

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchEntities(),
        fetchKnowledgeGraph()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEntities = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/memory/entities?limit=50`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setEntities(data.entities || []);
      }
    } catch (error) {
      console.error('Failed to fetch entities:', error);
    }
  };

  const fetchKnowledgeGraph = async () => {
    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/memory/knowledge-graph`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setGraphData(await response.json());
      }
    } catch (error) {
      console.error('Failed to fetch knowledge graph:', error);
    }
  };

  const searchMemory = async () => {
    if (!query.trim()) {
      toast.error('Please enter a search query');
      return;
    }

    setSearching(true);
    setResults(null);

    try {
      const token = localStorage.getItem('reasondms_token');
      const response = await fetch(`${API_URL}/api/agentic-extensions/memory/query`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: query.trim(),
          query_type: queryType,
          limit: 20
        })
      });

      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      setResults(data);
      toast.success(`Found ${data.results?.length || 0} results`);
    } catch (error) {
      console.error('Memory search error:', error);
      toast.error('Failed to search memory');
    } finally {
      setSearching(false);
    }
  };

  const getEntityIcon = (type) => {
    const Icon = ENTITY_ICONS[type?.toLowerCase()] || ENTITY_ICONS.default;
    return Icon;
  };

  const getEntityColor = (type) => {
    return ENTITY_COLORS[type?.toLowerCase()] || ENTITY_COLORS.default;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="memory-intelligence-panel">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-5 w-5 text-primary" />
            <CardTitle>Memory Intelligence</CardTitle>
          </div>
          <Button variant="outline" size="sm" onClick={fetchInitialData}>
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
        <CardDescription>
          Cross-document knowledge graph and entity memory
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Tabs */}
        <div className="flex gap-2 border-b">
          {[
            { id: 'search', label: 'Search', icon: Search },
            { id: 'entities', label: 'Entities', icon: Users },
            { id: 'graph', label: 'Knowledge Graph', icon: Network }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Search Tab */}
        {activeTab === 'search' && (
          <div className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search across all documents..."
                  onKeyDown={(e) => e.key === 'Enter' && searchMemory()}
                />
              </div>
              <Select value={queryType} onValueChange={setQueryType}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="entities">Entities</SelectItem>
                  <SelectItem value="vendors">Vendors</SelectItem>
                  <SelectItem value="clauses">Clauses</SelectItem>
                  <SelectItem value="patterns">Patterns</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={searchMemory} disabled={searching}>
                {searching ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Search className="h-4 w-4" />
                )}
              </Button>
            </div>

            {/* Search Results */}
            {results && (
              <div className="space-y-3">
                <div className="text-sm text-muted-foreground">
                  Found {results.results?.length || 0} results for "{results.query}"
                </div>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {results.results?.map((result, idx) => (
                    <div key={idx} className="p-3 border rounded-lg hover:border-primary/50 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium">{result.entity || result.value}</div>
                          <div className="text-sm text-muted-foreground mt-1">
                            {result.context || result.description}
                          </div>
                        </div>
                        {result.type && (
                          <Badge variant="outline" className={getEntityColor(result.type)}>
                            {result.type}
                          </Badge>
                        )}
                      </div>
                      {result.documents && result.documents.length > 0 && (
                        <div className="flex gap-2 mt-2 flex-wrap">
                          {result.documents.slice(0, 3).map((doc, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              <FileText className="h-3 w-3 mr-1" />
                              {doc.title || doc}
                            </Badge>
                          ))}
                          {result.documents.length > 3 && (
                            <Badge variant="secondary" className="text-xs">
                              +{result.documents.length - 3} more
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Entities Tab */}
        {activeTab === 'entities' && (
          <div className="space-y-4">
            {entities.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Database className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No entities indexed yet</p>
                <p className="text-sm">Process documents to build the knowledge base</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 max-h-96 overflow-y-auto">
                {entities.map((entity, idx) => {
                  const Icon = getEntityIcon(entity.type);
                  return (
                    <div
                      key={idx}
                      className={`p-3 rounded-lg border ${getEntityColor(entity.type)}`}
                    >
                      <div className="flex items-center gap-2">
                        <Icon className="h-4 w-4" />
                        <span className="font-medium text-sm truncate">{entity.value}</span>
                      </div>
                      <div className="text-xs mt-1 opacity-75">
                        {entity.count || 1} occurrence{(entity.count || 1) > 1 ? 's' : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Knowledge Graph Tab */}
        {activeTab === 'graph' && (
          <div className="space-y-4">
            {graphData ? (
              <>
                {/* Graph Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-muted/30 rounded-lg text-center">
                    <div className="text-2xl font-bold">{graphData.graph?.nodes?.length || graphData.stats?.total_nodes || 0}</div>
                    <div className="text-xs text-muted-foreground">Nodes</div>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg text-center">
                    <div className="text-2xl font-bold">{graphData.graph?.edges?.length || graphData.stats?.total_edges || 0}</div>
                    <div className="text-xs text-muted-foreground">Connections</div>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg text-center">
                    <div className="text-2xl font-bold">{graphData.stats?.total_documents || graphData.graph?.nodes?.filter(n => n.type === 'document')?.length || 0}</div>
                    <div className="text-xs text-muted-foreground">Documents</div>
                  </div>
                </div>

                {/* Interactive Knowledge Graph - pass graph sub-object */}
                <KnowledgeGraphVisualization 
                  graphData={graphData.graph || graphData}
                  onNodeClick={(node) => {
                    toast.info(`Selected: ${node.label} (${node.type})`);
                  }}
                  onRefresh={fetchKnowledgeGraph}
                />

                {/* Top Connections */}
                {graphData.top_connections && graphData.top_connections.length > 0 && (
                  <div className="space-y-2">
                    <h4 className="font-medium flex items-center gap-2">
                      <Link2 className="h-4 w-4 text-primary" />
                      Top Connections
                    </h4>
                    <div className="space-y-2">
                      {graphData.top_connections.map((conn, idx) => (
                        <div key={idx} className="flex items-center gap-2 p-2 bg-muted/30 rounded">
                          <Badge variant="outline">{conn.source}</Badge>
                          <span className="text-muted-foreground">→</span>
                          <Badge variant="outline">{conn.target}</Badge>
                          <span className="text-xs text-muted-foreground ml-auto">
                            {conn.weight || conn.count} links
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Brain className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>Knowledge graph not yet built</p>
                <p className="text-sm">Index documents to create connections</p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MemoryIntelligencePanel;
